import { onScopeDispose, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { getOrganizationSubscriptions } from '@/features/billing/api/get-organization-subscriptions'
import { organizationSubscriptionsQueryKey } from '@/features/billing/queries/useOrganizationSubscriptions'
import { isLiveSubscription } from '@/features/billing/types/billing'
import { completedCheckout, type PendingCheckout } from '@/features/billing/utils/pending-checkout'
import { MY_ORGANIZATIONS_QUERY_KEY } from '@/features/organizations/queries/useMyOrganizations'
import { ORGANIZATION_ONBOARDING_QUERY_KEY } from '@/features/organizations/queries/useOrganizationOnboarding'

const POLL_INTERVAL_MS = 3_000
const POLL_DURATION_MS = 60_000

/**
 * The provider's webhook records a subscription, and can arrive after the user is back from checkout.
 * This polls until the subscription appears, or gives up after a minute. The billing tab follows the
 * polled query; the no-subscription banner and creation blocks (`GET /organizations/my`) and the
 * onboarding steps refresh at the end.
 */
export function useCheckoutCompletionSync(): void {
  const queryClient = useQueryClient()

  let timer: ReturnType<typeof setTimeout> | null = null
  let isDisposed = false

  function stop(): void {
    if (timer !== null) clearTimeout(timer)
    timer = null
  }

  async function isRecorded({ organizationId, product }: PendingCheckout): Promise<boolean> {
    try {
      const subscriptions = await queryClient.fetchQuery({
        queryKey: organizationSubscriptionsQueryKey(organizationId),
        queryFn: () => getOrganizationSubscriptions(organizationId),
        staleTime: 0,
      })

      return subscriptions.some(
        (subscription) => subscription.product === product && isLiveSubscription(subscription),
      )
    } catch {
      return false
    }
  }

  function finish(): void {
    completedCheckout.value = null
    queryClient.invalidateQueries({ queryKey: MY_ORGANIZATIONS_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: ORGANIZATION_ONBOARDING_QUERY_KEY })
  }

  async function poll(checkout: PendingCheckout, deadline: number): Promise<void> {
    const recorded = await isRecorded(checkout)

    // Replaced or disposed while the request was running.
    if (isDisposed || completedCheckout.value !== checkout) return

    if (recorded || Date.now() >= deadline) {
      finish()
      return
    }

    timer = setTimeout(() => poll(checkout, deadline), POLL_INTERVAL_MS)
  }

  watch(
    completedCheckout,
    (checkout) => {
      stop()
      if (checkout) poll(checkout, Date.now() + POLL_DURATION_MS)
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    isDisposed = true
    stop()
  })
}
